import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: '#1A56DB', bg: '#EEF3FF', border: '#C3D2F7' },
  { value: 'in_progress', label: 'In Progress', color: '#92400E', bg: '#FFF7ED', border: '#FED7AA' },
  { value: 'done', label: 'Done', color: '#166534', bg: '#ECFDF3', border: '#BBF7D0' },
];

function Tasks() {
  const [businessId, setBusinessId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingNote, setEditingNote] = useState({});
  const [noteInputs, setNoteInputs] = useState({});

  useEffect(() => {
    const fetchBusinessId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: business } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single();
      if (business) setBusinessId(business.id);
    };
    fetchBusinessId();
  }, []);

  useEffect(() => {
    if (!businessId) return;
    const fetchTasks = async () => {
      setLoading(true);
      setError('');
      const { data, error: fetchError } = await supabase
        .from('leads')
        .select('id, customer_name, phone_number, need, urgency_level, best_callback_time, created_at, task_status, task_note')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (fetchError) { setError('Unable to load tasks. Please try again.'); setLoading(false); return; }
      setTasks(data || []);
      setLoading(false);
    };
    fetchTasks();
  }, [businessId]);

  const updateStatus = async (id, status) => {
    await supabase.from('leads').update({ task_status: status }).eq('id', id);
    setTasks(prev => prev.map(t => t.id === id ? { ...t, task_status: status } : t));
  };

  const saveNote = async (id) => {
    const note = noteInputs[id] || '';
    await supabase.from('leads').update({ task_note: note }).eq('id', id);
    setTasks(prev => prev.map(t => t.id === id ? { ...t, task_note: note } : t));
    setEditingNote(prev => ({ ...prev, [id]: false }));
  };

  const startEditNote = (id, currentNote) => {
    setNoteInputs(prev => ({ ...prev, [id]: currentNote || '' }));
    setEditingNote(prev => ({ ...prev, [id]: true }));
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const urgencyColor = (level) => {
    if (level === 'high') return { color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA' };
    if (level === 'medium') return { color: '#92400E', bg: '#FFF7ED', border: '#FED7AA' };
    return { color: '#166534', bg: '#ECFDF3', border: '#BBF7D0' };
  };

  const filteredTasks = filter === 'all' ? tasks : tasks.filter(t => (t.task_status || 'new') === filter);
  const counts = {
    all: tasks.length,
    new: tasks.filter(t => !t.task_status || t.task_status === 'new').length,
    in_progress: tasks.filter(t => t.task_status === 'in_progress').length,
    done: tasks.filter(t => t.task_status === 'done').length,
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: '32px', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>

        <header style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '32px', fontWeight: 800, color: '#0C1A2E', letterSpacing: '-0.5px' }}>Tasks</h1>
          <p style={{ marginTop: '6px', color: '#5A6B84', fontSize: '14px' }}>Track follow-ups so no lead falls through the cracks.</p>
        </header>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { value: 'all', label: 'All' },
            { value: 'new', label: 'New' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'done', label: 'Done' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              style={{
                padding: '7px 16px', borderRadius: '999px', border: '1px solid',
                borderColor: filter === f.value ? '#1A56DB' : '#DDE3F0',
                backgroundColor: filter === f.value ? '#EEF3FF' : '#fff',
                color: filter === f.value ? '#1A56DB' : '#5A6B84',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {f.label} <span style={{ marginLeft: '4px', opacity: 0.7 }}>({counts[f.value]})</span>
            </button>
          ))}
        </div>

        {error && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '14px' }}>{error}</div>
        )}

        {filteredTasks.length === 0 && !loading ? (
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <div style={{ fontWeight: 600, color: '#0C1A2E', marginBottom: '6px', fontSize: '16px' }}>
              {filter === 'done' ? 'No completed tasks yet' : filter === 'new' ? 'No new tasks' : 'No tasks here'}
            </div>
            <div style={{ fontSize: '14px', color: '#5A6B84' }}>Tasks appear here when customers leave their info after a missed call.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredTasks.map(task => {
              const status = task.task_status || 'new';
              const urgency = urgencyColor(task.urgency_level);
              const isEditingNote = editingNote[task.id];

              return (
                <div key={task.id} style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', padding: '20px 24px', boxShadow: '0 2px 8px rgba(26,86,219,0.04)', opacity: status === 'done' ? 0.7 : 1 }}>

                  {/* Top row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: status === 'done' ? '#9AABBE' : '#0C1A2E', textDecoration: status === 'done' ? 'line-through' : 'none' }}>
                          {task.customer_name || 'Unknown caller'}
                        </div>
                        {task.urgency_level && (
                          <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, backgroundColor: urgency.bg, color: urgency.color, border: `1px solid ${urgency.border}`, textTransform: 'capitalize' }}>
                            {task.urgency_level}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#5A6B84' }}>
                        {task.phone_number || '—'} · {formatDate(task.created_at)}
                      </div>
                    </div>

                    {/* Call button */}
                    {task.phone_number && (
                      <a href={`tel:${task.phone_number}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '7px 14px', borderRadius: '999px', backgroundColor: '#22C55E', color: '#fff', fontSize: '13px', fontWeight: 700, textDecoration: 'none', flexShrink: 0 }}>
                        📞 Call
                      </a>
                    )}
                  </div>

                  {/* What they needed */}
                  {task.need && (
                    <div style={{ padding: '10px 14px', backgroundColor: '#F4F7FF', borderRadius: '8px', fontSize: '14px', color: '#3D4F66', marginBottom: '14px', lineHeight: 1.5 }}>
                      {task.need}
                      {task.best_callback_time && (
                        <span style={{ color: '#9AABBE', marginLeft: '6px' }}>· Best time: {task.best_callback_time}</span>
                      )}
                    </div>
                  )}

                  {/* Status buttons */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {STATUS_OPTIONS.map(s => (
                      <button
                        key={s.value}
                        onClick={() => updateStatus(task.id, s.value)}
                        style={{
                          padding: '6px 14px', borderRadius: '999px', border: '1px solid',
                          borderColor: status === s.value ? s.border : '#DDE3F0',
                          backgroundColor: status === s.value ? s.bg : '#fff',
                          color: status === s.value ? s.color : '#9AABBE',
                          fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          transition: 'all 0.15s',
                        }}
                      >
                        {status === s.value && '✓ '}{s.label}
                      </button>
                    ))}
                  </div>

                  {/* Note */}
                  {isEditingNote ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        value={noteInputs[task.id] || ''}
                        onChange={e => setNoteInputs(prev => ({ ...prev, [task.id]: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && saveNote(task.id)}
                        placeholder="Add initials or a quick note e.g. JM - left voicemail"
                        autoFocus
                        style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #DDE3F0', fontSize: '13px', fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none', color: '#0C1A2E' }}
                      />
                      <button onClick={() => saveNote(task.id)} style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', backgroundColor: '#F97316', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setEditingNote(prev => ({ ...prev, [task.id]: false }))} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #DDE3F0', backgroundColor: '#fff', color: '#5A6B84', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  ) : (
                    <div
                      onClick={() => startEditNote(task.id, task.task_note)}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', border: '1px dashed #DDE3F0', cursor: 'pointer', fontSize: '13px', color: task.task_note ? '#3D4F66' : '#9AABBE', backgroundColor: task.task_note ? '#F4F7FF' : 'transparent' }}
                    >
                      <span>✏️</span>
                      <span>{task.task_note || 'Add a note or initials...'}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Tasks;